import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Home as HomeIcon, Snowflake, Wrench, Truck, HeartHandshake, Dog, Laptop,
  ShoppingCart, PartyPopper, Car, Clock, Shirt, Hammer, AlertTriangle,
  ArrowRight, Search,
} from "lucide-react";
import { Logo } from "../components/Logo";

const SECTIONS = [
  {
    id: "maison",
    icon: HomeIcon,
    title: "Maison & entretien",
    items: [
      "Ménage léger / grand ménage",
      "Laver planchers, vitres",
      "Ranger / organiser",
      "Sortir les poubelles",
      "Nettoyer après travaux",
      "Nettoyage de déménagement",
      "Laver auto à domicile",
    ],
  },
  {
    id: "exterieur",
    icon: Snowflake,
    title: "Extérieur (très fort en région)",
    items: [
      "Déneiger entrée / toit / auto",
      "Tondre gazon",
      "Ramasser feuilles",
      "Couper branches",
      "Pelleter gravier / terre",
      "Nettoyer gouttières",
      "Monter / démonter abri tempo",
      "Installer décorations saisonnières",
    ],
  },
  {
    id: "reparations",
    icon: Wrench,
    title: "Réparations & petits travaux",
    items: [
      "Monter meubles (IKEA, etc.)",
      "Installer étagères",
      "Changer luminaires",
      "Réparer porte / poignée",
      "Installer rideaux",
      "Petits travaux de peinture",
      "Réparer clôture",
      "Aide rénovation légère",
    ],
  },
  {
    id: "transport",
    icon: Truck,
    title: "Transport & commissions",
    items: [
      "Aller chercher colis",
      "Livraison locale",
      "Aller à l'épicerie",
      "Aller à la SAQ / pharmacie",
      "Transport petit meuble",
      "Dépôt recyclage",
      "Aller porter quelque chose",
    ],
  },
  {
    id: "aide-personnes",
    icon: HeartHandshake,
    title: "Aide aux personnes",
    items: [
      "Accompagner rendez-vous",
      "Faire commissions pour quelqu'un",
      "Visite de courtoisie",
      "Aide à domicile légère",
      "Lecture / compagnie",
      "Aide avec téléphone / technologie",
    ],
  },
  {
    id: "animaux",
    icon: Dog,
    title: "Garde & animaux",
    items: [
      "Garder enfants (court terme)",
      "Aller chercher à l'école",
      "Promener chien",
      "Nourrir animaux",
      "Garder animaux à domicile",
      "Nettoyer litière",
    ],
  },
  {
    id: "numerique",
    icon: Laptop,
    title: "Aide numérique",
    items: [
      "Installer téléphone / app",
      "Aide informatique",
      "Configurer Wi-Fi",
      "Aide CV / documents",
      "Publier annonces (Marketplace, etc.)",
    ],
  },
  {
    id: "economie",
    icon: ShoppingCart,
    title: "Économie & optimisation",
    items: [
      "Comparer prix",
      "Faire épicerie optimisée",
      "Chercher deals locaux",
      "Acheter en gros pour quelqu'un",
      "Planifier achats",
    ],
  },
  {
    id: "evenements",
    icon: PartyPopper,
    title: "Événements & aide ponctuelle",
    items: [
      "Installer décorations",
      "Aider à organiser",
      "Monter tables / chaises",
      "Servir / aider pendant événement",
      "Nettoyer après",
    ],
  },
  {
    id: "auto",
    icon: Car,
    title: "Déplacements & auto",
    items: [
      "Aller porter quelqu'un",
      "Aller chercher quelqu'un",
      "Aide changement de pneu",
      "Booster batterie",
      "Nettoyage auto",
    ],
  },
  {
    id: "temps",
    icon: Clock,
    title: "Tâches « temps = argent »",
    items: [
      "Attendre en file",
      "Réserver une place",
      "Aller vérifier quelque chose",
      "Prendre rendez-vous pour quelqu'un",
    ],
  },
  {
    id: "quotidien",
    icon: Shirt,
    title: "Tâches du quotidien",
    items: [
      "Faire lessive",
      "Plier vêtements",
      "Préparer repas simple",
      "Aller poster colis",
      "Aller à la banque",
    ],
  },
  {
    id: "costauds",
    icon: Hammer,
    title: "Travaux plus costauds (selon compétences)",
    items: [
      "Aide déménagement",
      "Transport lourd",
      "Aide chantier",
      "Coupe de bois",
      "Travaux saisonniers",
    ],
  },
];

const NOT_ALLOWED = [
  "Travaux dangereux sans qualification",
  "Services illégaux",
  "Soins médicaux",
  "Travaux nécessitant licence (électricien, plomberie complexe, etc.)",
];

export default function Ideas() {
  const [query, setQuery] = useState("");

  const filtered = query.trim().length === 0
    ? SECTIONS
    : SECTIONS
        .map((s) => ({
          ...s,
          items: s.items.filter((i) => i.toLowerCase().includes(query.toLowerCase())),
        }))
        .filter((s) => s.items.length > 0);

  return (
    <div className="min-h-screen bg-canvas">
      <header className="border-b border-[#E2E8F0] bg-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="ideas-logo"><Logo size={36} /></Link>
          <Link to="/login" className="btn-outline py-2 px-4 text-sm" data-testid="ideas-login">Connexion</Link>
        </div>
      </header>

      <section className="bg-navy text-white relative overflow-hidden noise">
        <div className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-lime/20 blur-3xl" />
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-20 relative">
          <span className="chip-lime" data-testid="ideas-tag">Idées de tâches</span>
          <h1 className="mt-6 text-4xl md:text-6xl font-heading font-black leading-tight" data-testid="ideas-title">
            Tu peux demander <span className="text-lime">presque n'importe quoi.</span>
          </h1>
          <p className="mt-4 text-white/80 text-lg md:text-xl max-w-2xl">
            Simple, légal, faisable. Voici plus de 80 exemples concrets pour t'inspirer.
            Les helpers près de chez toi peuvent t'aider sur ces tâches — et bien d'autres.
          </p>

          <div className="mt-8 relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-navy/60" size={18} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cherche une idée (ex: déneiger, animaux...)"
              className="w-full rounded-full border-2 border-transparent bg-white text-navy placeholder:text-navy/40 pl-12 pr-4 py-3 font-medium focus:outline-none focus:ring-2 focus:ring-lime"
              data-testid="ideas-search"
            />
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-12 space-y-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.id}
                className="card-surface p-5 group hover:ring-2 hover:ring-lime transition-all"
                data-testid={`ideas-section-${s.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-navy text-lime flex items-center justify-center shrink-0 group-hover:rotate-3 transition-transform">
                    <Icon size={22} />
                  </div>
                  <h2 className="font-heading font-black text-navy text-lg leading-tight">{s.title}</h2>
                </div>
                <ul className="mt-4 space-y-1.5 text-sm text-[#475569]">
                  {s.items.map((item) => (
                    <li key={item} className="flex gap-2">
                      <span className="text-lime font-black shrink-0">›</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="card-surface p-8 text-center text-[#475569]" data-testid="ideas-empty">
            Aucune idée ne correspond à « {query} ». Essaie un autre mot — ou propose ta demande, c'est probablement faisable !
          </div>
        )}

        <section className="rounded-2xl bg-red-50 border-2 border-red-200 p-6 md:p-8" data-testid="ideas-not-allowed">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-red-500 text-white flex items-center justify-center shrink-0">
              <AlertTriangle size={22} />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-black text-2xl text-red-900">Non autorisé sur Go Helper</h2>
              <p className="text-red-800/80 mt-1 text-sm">
                Pour ta sécurité et celle des helpers, ces demandes ne sont pas acceptées sur la plateforme.
              </p>
              <ul className="mt-4 grid md:grid-cols-2 gap-2 text-sm text-red-900">
                {NOT_ALLOWED.map((n) => (
                  <li key={n} className="flex gap-2 bg-white/60 rounded-lg p-2.5">
                    <span className="font-black shrink-0">✕</span>
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="rounded-3xl bg-navy text-white p-8 md:p-12 text-center relative overflow-hidden noise" data-testid="ideas-cta">
          <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy to-[#0E2440]" />
          <div className="absolute -top-20 -left-20 h-64 w-64 rounded-full bg-lime/20 blur-3xl" />
          <div className="relative">
            <h2 className="font-heading font-black text-3xl md:text-5xl">
              Et bien plus encore — <span className="text-lime">propose ta demande.</span>
            </h2>
            <p className="text-white/80 mt-3 max-w-xl mx-auto">
              Tu ne trouves pas exactement ce que tu cherches ? Décris ta tâche dans tes mots, un helper local pourra t'aider.
            </p>
            <Link to="/tasks/new" className="btn-primary mt-8 inline-flex" data-testid="ideas-cta-publish">
              Publier ma tâche <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
